import { useId, type CSSProperties } from "react";
import { cn } from "@/lib/cn";

/**
 * Mirror of
 * `merchant-portal-web/src/pages/settings/general/components/Coupons/CouponCards.tsx`.
 * Without a monorepo, preview and storefront match only if the SVG geometry is the same file;
 * the default colors here are CSS-variable palette tokens instead of fixed hex.
 */

export type CouponCardProps = {
  /** Text shown above the discount value. */
  title?: string;
  /** Main coupon value, e.g. "10%" or "R$ 15". */
  discount?: string;
  /** Coupon code on the stub. */
  code?: string;
  /** Condition or note shown at the bottom. */
  description?: string;
  /** Curved band color. */
  accentColor?: string;
  /** Icon block color. */
  iconBackgroundColor?: string;
  /** Icon color. */
  iconColor?: string;
  /** Coupon background color. */
  backgroundColor?: string;
  /** Discount value color. */
  textColor?: string;
  /** Title and condition color — softer than the value. */
  mutedTextColor?: string;
  /** Outline and seam color. */
  borderColor?: string;
  /**
   * Corner radius in `viewBox` units. The renderer knows real width and converts
   * with {@link couponCornerRadiusUnits}.
   */
  cornerRadius?: number;
  /**
   * Footer drawing imposed from outside — a ROW of coupons decides together.
   * See {@link sharedCouponFooterStyle}. Absent, the coupon decides alone.
   */
  footerStyle?: CouponFooterStyle;
  className?: string;
  style?: CSSProperties;
};

/**
 * OUTLINE
 */

/** Coordinate system every number on this piece lives in. */
const VIEWBOX_WIDTH = 360;

/** Coupon box inside the `viewBox`, with outline padding. */
const CARD_LEFT = 6;
const CARD_RIGHT = 354;
const CARD_TOP = 5;
const CARD_BOTTOM = 191;

/** Where side notches start/end and how deep they bite. */
const NOTCH_TOP = 82;
const NOTCH_BOTTOM = 110;
const NOTCH_DEPTH = 16;

/**
 * Storefront card corner: the `rounded-[5px]` ItemOfferCard, CategoryImageCard,
 * and FilledCart already use.
 */
const CARD_CORNER_PX = 5;

/**
 * Radius ceiling. A tiny piece would ask for a radius larger than the notches
 * and warp the outline.
 */
const CORNER_RADIUS_MAX = 24;

/**
 * Radius on the MEDIUM coupon (11.6rem ≈ 162px at 14px root) — the showcase
 * anchor step. Used when drawing without a known width.
 */
const CORNER_RADIUS_DEFAULT = 11.1;

/**
 * Corner radius in `viewBox` units for a piece drawn at `renderedWidthPx`.
 * "5px" and "5 units" are NOT the same: the SVG scales with width, so a fixed
 * viewBox radius yields different px per size. Conversion stays outside and
 * enters via the prop.
 */
export function couponCornerRadiusUnits(renderedWidthPx: number): number {
  if (!(renderedWidthPx > 0)) return CORNER_RADIUS_DEFAULT;
  const units = (CARD_CORNER_PX * VIEWBOX_WIDTH) / renderedWidthPx;
  return Math.min(CORNER_RADIUS_MAX, Math.round(units * 100) / 100);
}

/**
 * Outline: box, four corners, and two seam notches. Built in code (not a fixed
 * string) because radius varies. With `radius = 18` the `d` matches the pre-prop
 * path number-for-number.
 */
export function couponOutline(radius: number): string {
  const r = Math.max(0, Math.min(CORNER_RADIUS_MAX, radius));
  const k = r * 0.5556;
  const n = (value: number) => Math.round(value * 100) / 100;

  return [
    `M${n(CARD_LEFT + r)} ${CARD_TOP}`,
    `H${n(CARD_RIGHT - r)}`,
    `C${n(CARD_RIGHT - r + k)} ${CARD_TOP} ${CARD_RIGHT} ${n(CARD_TOP + r - k)} ${CARD_RIGHT} ${n(CARD_TOP + r)}`,
    `V${NOTCH_TOP}`,
    `C${CARD_RIGHT - NOTCH_DEPTH} ${NOTCH_TOP} ${CARD_RIGHT - NOTCH_DEPTH} ${NOTCH_BOTTOM} ${CARD_RIGHT} ${NOTCH_BOTTOM}`,
    `V${n(CARD_BOTTOM - r)}`,
    `C${CARD_RIGHT} ${n(CARD_BOTTOM - r + k)} ${n(CARD_RIGHT - r + k)} ${CARD_BOTTOM} ${n(CARD_RIGHT - r)} ${CARD_BOTTOM}`,
    `H${n(CARD_LEFT + r)}`,
    `C${n(CARD_LEFT + r - k)} ${CARD_BOTTOM} ${CARD_LEFT} ${n(CARD_BOTTOM - r + k)} ${CARD_LEFT} ${n(CARD_BOTTOM - r)}`,
    `V${NOTCH_BOTTOM}`,
    `C${CARD_LEFT + NOTCH_DEPTH} ${NOTCH_BOTTOM} ${CARD_LEFT + NOTCH_DEPTH} ${NOTCH_TOP} ${CARD_LEFT} ${NOTCH_TOP}`,
    `V${n(CARD_TOP + r)}`,
    `C${CARD_LEFT} ${n(CARD_TOP + r - k)} ${n(CARD_LEFT + r - k)} ${CARD_TOP} ${n(CARD_LEFT + r)} ${CARD_TOP}`,
    "Z",
  ].join("");
}

/**
 * TEXT ALWAYS FITS
 *
 * SVG does not wrap, shrink, or truncate. The footer is two fixed-x texts that
 * OVERLAP when their widths exceed the gap. Measure with a glyph TABLE before
 * paint (not `getComputedTextLength`) so the first paint is correct and tests
 * stay pure. Shrink font first (one factor for both); only at the floor compress
 * glyphs. Compression is ugly; overlap is unreadable.
 */

/**
 * Montserrat glyph advance in HUNDREDTHS of `em` at weight 400. MEASURED in
 * Chromium with the same Google Fonts Montserrat both repos load. Per-glyph, not
 * per category — codes are uppercase+digits where categories fail. Integer
 * hundredths: the piece lives on a 360-unit grid.
 */
const MONTSERRAT_CENTI_EM: Record<string, number> = {
  " ": 26, ".": 21, ",": 21, ":": 21, ";": 21, "!": 21, "'": 21,
  "-": 38, "/": 34, "(": 35, ")": 35, $: 62, "%": 83,
  0: 66, 1: 36, 2: 57, 3: 56, 4: 66, 5: 57, 6: 61, 7: 59, 8: 64, 9: 61,
  A: 72, B: 75, C: 71, D: 83, E: 67, F: 63, G: 77, H: 81, I: 30, J: 50,
  K: 71, L: 59, M: 95, N: 81, O: 84, P: 72, Q: 84, R: 72, S: 61, T: 57,
  U: 79, V: 70, W: 111, X: 66, Y: 64, Z: 65,
  a: 59, b: 68, c: 56, d: 68, e: 60, f: 34, g: 69, h: 68, i: 27, j: 27,
  k: 60, l: 27, m: 106, n: 68, o: 63, p: 68, q: 68, r: 40, s: 49, t: 41,
  u: 67, v: 54, w: 88, x: 53, y: 54, z: 51,
};

/** Average uppercase advance — safe guess for an unmeasured glyph. */
const UNKNOWN_GLYPH_EM = 0.7;

function glyphEm(glyph: string): number {
  const measured = MONTSERRAT_CENTI_EM[glyph];
  if (measured !== undefined) return measured / 100;

  const base = glyph.normalize("NFD").replace(/[̀-ͯ]/g, "");
  const stripped = base.length === 1 ? MONTSERRAT_CENTI_EM[base] : undefined;
  return stripped !== undefined ? stripped / 100 : UNKNOWN_GLYPH_EM;
}

/**
 * How weight fattens the letter. Measured 400→650: ~2.3% (this divisor).
 * Variable Montserrat thickens the stroke more than it widens advance.
 */
const WEIGHT_WIDTH_DIVISOR = 10000;

/**
 * Safety bias. The table is weight 400 with a linear weight model, so high
 * weights under-estimate — the only error that brings back overlap. 4% turns
 * the worst measured shortfall into a small surplus.
 */
const WIDTH_SAFETY = 1.04;

/**
 * Estimated text width in `viewBox` units. Weight matters: the same code at 650
 * is wider than at 430, and the code is the heaviest text on the piece.
 */
export function estimateSvgTextWidth(
  text: string,
  fontSize: number,
  fontWeight: number,
  letterSpacing = 0,
): number {
  const glyphs = Array.from(text);
  if (glyphs.length === 0) return 0;
  const em = glyphs.reduce((total, glyph) => total + glyphEm(glyph), 0);
  const weightFactor = 1 + (fontWeight - 400) / WEIGHT_WIDTH_DIVISOR;
  return (
    em * fontSize * weightFactor * WIDTH_SAFETY + letterSpacing * (glyphs.length - 1)
  );
}

/** Row font size, and each text's width cap only when size hits the floor. */
export interface FittedTextRow {
  fontSize: number;
  leftLength?: number;
  rightLength?: number;
}

export interface FitTextRowInput {
  left: string;
  /** Empty when the row has a single text — then the gutter is not charged. */
  right: string;
  span: number;
  gutter: number;
  fontSize: number;
  minFontSize: number;
  leftWeight: number;
  rightWeight: number;
  letterSpacing: number;
}

/**
 * Fits two texts that share a fixed-width row. The GUTTER does not shrink with
 * the font — it marks "two texts"; scaled away it glues code and condition.
 */
export function fitTextRow({
  left,
  right,
  span,
  gutter,
  fontSize,
  minFontSize,
  leftWeight,
  rightWeight,
  letterSpacing,
}: FitTextRowInput): FittedTextRow {
  const leftWidth = estimateSvgTextWidth(left, fontSize, leftWeight, letterSpacing);
  const rightWidth = estimateSvgTextWidth(right, fontSize, rightWeight, letterSpacing);
  const total = leftWidth + rightWidth;
  const room = span - (left && right ? gutter : 0);
  if (total <= 0 || total <= room) return { fontSize };

  const scale = Math.max(minFontSize / fontSize, room / total);
  const fitted = Math.round(fontSize * scale * 100) / 100;
  if (total * scale <= room) return { fontSize: fitted };

  const cap = (width: number) => Math.round(((room * width) / total) * 100) / 100;
  return {
    fontSize: fitted,
    leftLength: left ? cap(leftWidth) : undefined,
    rightLength: right ? cap(rightWidth) : undefined,
  };
}

/** Where the footer line starts and ends, in `viewBox` units. */
const FOOTER_START = 68;
const FOOTER_END = 310;
const FOOTER_GUTTER = 14;
const FOOTER_FONT_SIZE = 23;
/**
 * Footer font floor. Below this the coupon is unreadable at arm's length on a
 * phone in hand.
 */
const FOOTER_MIN_FONT_SIZE = 15;
const FOOTER_LETTER_SPACING = -0.25;

/** Text baseline when code and minimum share one line. */
const FOOTER_BASELINE = 148;
/** Two baselines in stacked mode, between the seam (110) and cut (191). */
const FOOTER_STACK_TOP_BASELINE = 138;
const FOOTER_STACK_BOTTOM_BASELINE = 170;

/**
 * Below this size a single line is not worth it. Long code + minimum need more
 * units than the span; stacking gives each text the FULL span. The step is where
 * shrinking stops accommodating and starts hiding.
 */
const FOOTER_STACK_BELOW = 18;

/** Discount value: starts at the label `x` and stops before the outline. */
const VALUE_START = 116;
const VALUE_END = 340;
const VALUE_FONT_SIZE = 30;
const VALUE_MIN_FONT_SIZE = 20;
const VALUE_LETTER_SPACING = -0.8;

const CODE_WEIGHT = 650;
const CONDITION_WEIGHT = 430;

/** One text's font size, and its width cap when size hits the floor. */
export interface FittedText {
  fontSize: number;
  textLength?: number;
}

const FOOTER_SPAN = FOOTER_END - FOOTER_START;

export interface CouponFooter {
  /** `row`: both share the line. `stack`: one line each. */
  layout: "row" | "stack";
  code: FittedText;
  condition: FittedText;
}

/**
 * Footer DECISION — layout and font, without per-text width caps. What a coupon
 * row shares. ONE font size for both texts (weight separates them, not size).
 */
export interface CouponFooterStyle {
  layout: "row" | "stack";
  fontSize: number;
}

/** Font size that lets code and condition share ONE line. */
function rowFontSize(code: string, description: string): number {
  return fitTextRow({
    left: code,
    right: description,
    span: FOOTER_SPAN,
    gutter: FOOTER_GUTTER,
    fontSize: FOOTER_FONT_SIZE,
    minFontSize: FOOTER_MIN_FONT_SIZE,
    leftWeight: CODE_WEIGHT,
    rightWeight: CONDITION_WEIGHT,
    letterSpacing: FOOTER_LETTER_SPACING,
  }).fontSize;
}

/** Largest font that lets one text fit ALONE in the span. */
function fontSizeAlone(text: string, weight: number): number {
  return fitTextRow({
    left: text,
    right: "",
    span: FOOTER_SPAN,
    gutter: 0,
    fontSize: FOOTER_FONT_SIZE,
    minFontSize: FOOTER_MIN_FONT_SIZE,
    leftWeight: weight,
    rightWeight: weight,
    letterSpacing: FOOTER_LETTER_SPACING,
  }).fontSize;
}

/** Empilhados, os dois seguem a linha mais apertada das duas. */
function stackFontSize(code: string, description: string): number {
  return Math.min(
    fontSizeAlone(code, CODE_WEIGHT),
    fontSizeAlone(description, CONDITION_WEIGHT),
  );
}

/**
 * Style ONE coupon would ask for alone. Single line while the font holds; below
 * {@link FOOTER_STACK_BELOW}, one line each. Stacking stops the worst case from
 * becoming a blur of compressed glyphs.
 */
export function couponFooterStyle(code: string, description: string): CouponFooterStyle {
  const row = rowFontSize(code, description);
  if (!code || row >= FOOTER_STACK_BELOW) return { layout: "row", fontSize: row };
  return { layout: "stack", fontSize: stackFontSize(code, description) };
}

/**
 * Footer style for the whole ROW: the worst case rules everyone. Coupons sit
 * side by side — per-coupon decisions misalign the row. Same reason rail card
 * height is fixed per size. Applies to layout AND font size.
 */
export function sharedCouponFooterStyle(
  entries: ReadonlyArray<{ code: string; description: string }>,
): CouponFooterStyle {
  const stacked = entries.some(
    (entry) => couponFooterStyle(entry.code, entry.description).layout === "stack",
  );
  const sizes = entries.map((entry) =>
    stacked
      ? stackFontSize(entry.code, entry.description)
      : rowFontSize(entry.code, entry.description),
  );

  return {
    layout: stacked ? "stack" : "row",
    fontSize: Math.min(FOOTER_FONT_SIZE, ...sizes),
  };
}

/** O teto de largura de um texto, ou nada quando ele cabe inteiro. */
function textCap(
  text: string,
  fontSize: number,
  weight: number,
  room: number,
): number | undefined {
  if (!text) return undefined;
  const width = estimateSvgTextWidth(text, fontSize, weight, FOOTER_LETTER_SPACING);
  return width <= room ? undefined : Math.round(room * 100) / 100;
}

/**
 * Applies an already-chosen style to THIS coupon. Font size is not renegotiated;
 * only the width cap still depends on this text so a long code cannot pierce
 * the piece at the neighbor's size.
 */
export function couponFooterFromStyle(
  code: string,
  description: string,
  style: CouponFooterStyle,
): CouponFooter {
  const { fontSize } = style;

  if (style.layout === "stack") {
    return {
      layout: "stack",
      code: {
        fontSize,
        textLength: textCap(code, fontSize, CODE_WEIGHT, FOOTER_SPAN),
      },
      condition: {
        fontSize,
        textLength: textCap(description, fontSize, CONDITION_WEIGHT, FOOTER_SPAN),
      },
    };
  }

  const room = FOOTER_SPAN - (code && description ? FOOTER_GUTTER : 0);
  const codeWidth = estimateSvgTextWidth(code, fontSize, CODE_WEIGHT, FOOTER_LETTER_SPACING);
  const conditionWidth = estimateSvgTextWidth(
    description,
    fontSize,
    CONDITION_WEIGHT,
    FOOTER_LETTER_SPACING,
  );
  const total = codeWidth + conditionWidth;
  if (total <= room) {
    return {
      layout: "row",
      code: { fontSize },
      condition: { fontSize },
    };
  }

  const share = (width: number) => Math.round(((room * width) / total) * 100) / 100;
  return {
    layout: "row",
    code: { fontSize, textLength: code ? share(codeWidth) : undefined },
    condition: {
      fontSize,
      textLength: description ? share(conditionWidth) : undefined,
    },
  };
}

/** Footer for a coupon drawn alone, with no row to obey. */
export function fitCouponFooter(code: string, description: string): CouponFooter {
  return couponFooterFromStyle(code, description, couponFooterStyle(code, description));
}

/**
 * Responsive coupon drawn entirely in SVG. The viewBox keeps shape, side cuts,
 * and curved band proportional at any width. No external CSS or icon package.
 */
export function CouponCard({
  title = "Desconto",
  discount = "10%",
  code,
  description = "Sem mínimo",
  accentColor = "rgb(var(--color-danger))",
  iconBackgroundColor = "rgb(var(--color-success) / 0.15)",
  iconColor = "rgb(var(--color-success))",
  backgroundColor = "rgb(var(--color-card))",
  textColor = "rgb(var(--color-card-text))",
  mutedTextColor,
  borderColor = "rgb(var(--color-border))",
  cornerRadius = CORNER_RADIUS_DEFAULT,
  footerStyle,
  className,
  style,
}: CouponCardProps) {
  const muted = mutedTextColor ?? textColor;
  const reactId = useId().replace(/:/g, "");
  const clipId = `coupon-clip-${reactId}`;
  const shadowId = `coupon-shadow-${reactId}`;
  const ribbonId = `coupon-ribbon-${reactId}`;

  const outline = couponOutline(cornerRadius);
  const footer = footerStyle
    ? couponFooterFromStyle(code ?? "", description, footerStyle)
    : fitCouponFooter(code ?? "", description);
  const stacked = footer.layout === "stack";

  const value = fitTextRow({
    left: discount,
    right: "",
    span: VALUE_END - VALUE_START,
    gutter: 0,
    fontSize: VALUE_FONT_SIZE,
    minFontSize: VALUE_MIN_FONT_SIZE,
    leftWeight: CODE_WEIGHT,
    rightWeight: CONDITION_WEIGHT,
    letterSpacing: VALUE_LETTER_SPACING,
  });

  return (
    <svg
      className={cn("block h-auto w-full", className)}
      style={style}
      viewBox="0 0 360 196"
      role="img"
      aria-label={`${title}: ${discount}. ${[code, description].filter(Boolean).join(". ")}`}
      xmlns="http://www.w3.org/2000/svg"
    >
      <title>{`${title}: ${discount}. ${[code, description].filter(Boolean).join(". ")}`}</title>
      <defs>
        <clipPath id={clipId}>
          <path d={outline} />
        </clipPath>

        <filter
          id={shadowId}
          x="-10%"
          y="-15%"
          width="120%"
          height="135%"
          colorInterpolationFilters="sRGB"
        >
          <feDropShadow dx="0" dy="3" stdDeviation="4" floodColor="rgb(var(--color-card-text))" floodOpacity="0.10" />
        </filter>

        <linearGradient id={ribbonId} x1="0" y1="1" x2="1" y2="0">
          <stop offset="0" stopColor={accentColor} stopOpacity="0.98" />
          <stop offset="0.52" stopColor={accentColor} stopOpacity="0.91" />
          <stop offset="1" stopColor={accentColor} stopOpacity="0.98" />
        </linearGradient>
      </defs>

      <g filter={`url(#${shadowId})`}>
        <path
          d={outline}
          fill={backgroundColor}
          stroke={borderColor}
          strokeWidth="2"
        />
      </g>

      <g clipPath={`url(#${clipId})`}>
        
        <path
          d="M116 103H310"
          stroke={borderColor}
          strokeWidth="2"
          strokeDasharray="8 9"
          opacity="0.72"
        />

        
        <rect x="43" y="28" width="58" height="58" rx="17" fill={iconBackgroundColor} />

        
        <g transform="translate(57 43)" fill={iconColor}>
          <path d="M1 5.5A3.5 3.5 0 0 1 4.5 2h22A3.5 3.5 0 0 1 30 5.5v3.2a4.8 4.8 0 0 0 0 8.6v3.2a3.5 3.5 0 0 1-3.5 3.5h-22A3.5 3.5 0 0 1 1 20.5v-3.2a4.8 4.8 0 0 0 0-8.6V5.5Z" />
          <rect x="13.8" y="5" width="2.4" height="4" rx="1.2" fill={iconBackgroundColor} />
          <rect x="13.8" y="11" width="2.4" height="4" rx="1.2" fill={iconBackgroundColor} />
          <rect x="13.8" y="17" width="2.4" height="4" rx="1.2" fill={iconBackgroundColor} />
        </g>

        
        <g fontFamily="Montserrat, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif">
          <text
            x={VALUE_START}
            y="47"
            fontSize="23"
            fontWeight="450"
            letterSpacing="-0.25"
            fill={muted}
            fillOpacity="0.55"
          >
            {title}
          </text>
          <text
            x={VALUE_START}
            y="78"
            fontSize={value.fontSize}
            fontWeight={CODE_WEIGHT}
            letterSpacing={VALUE_LETTER_SPACING}
            textLength={value.leftLength}
            lengthAdjust={value.leftLength ? "spacingAndGlyphs" : undefined}
            fill={textColor}
          >
            {discount}
          </text>
          {code ? (
            <text
              x={FOOTER_START}
              y={stacked ? FOOTER_STACK_TOP_BASELINE : FOOTER_BASELINE}
              fontSize={footer.code.fontSize}
              fontWeight={CODE_WEIGHT}
              letterSpacing={FOOTER_LETTER_SPACING}
              textLength={footer.code.textLength}
              lengthAdjust={footer.code.textLength ? "spacingAndGlyphs" : undefined}
              fill={textColor}
            >
              {code}
            </text>
          ) : null}
          
          <text
            x={code && !stacked ? FOOTER_END : FOOTER_START}
            y={stacked ? FOOTER_STACK_BOTTOM_BASELINE : FOOTER_BASELINE}
            fontSize={footer.condition.fontSize}
            fontWeight={CONDITION_WEIGHT}
            letterSpacing={FOOTER_LETTER_SPACING}
            textAnchor={code && !stacked ? "end" : "start"}
            textLength={footer.condition.textLength}
            lengthAdjust={footer.condition.textLength ? "spacingAndGlyphs" : undefined}
            fill={muted}
            fillOpacity="0.75"
          >
            {description}
          </text>
        </g>
      </g>
    </svg>
  );
}

export default CouponCard;
